import { ApiError, handleApiError } from "./errorHandler";
import { ENV } from "@/config/env";
import { logoutUser, queryClient } from "./queryClient";
import { toast } from "@/utils/toast.service";

export interface RequestOptions extends Omit<RequestInit, "body"> {
  params?: Record<string, any>;
  body?: any;
  showToast?: boolean;
  responseType?: "json" | "blob" | "text";
}

const getBaseUrl = (): string => {
  const rawUrl = ENV.BASE_URL;
  return rawUrl.replace(/\/$/, "");
};

const getAuthToken = (): string | null => {
  return (
    localStorage.getItem("token") || sessionStorage.getItem("token") || null
  );
};

const getRefreshToken = (): string | null => {
  return (
    localStorage.getItem("refreshToken") ||
    sessionStorage.getItem("refreshToken") ||
    null
  );
};

const buildQueryString = (params?: Record<string, any>): string => {
  if (!params) return "";
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const val = params[key];
    if (val !== undefined && val !== null && val !== "") {
      if (Array.isArray(val)) {
        val.forEach((item) => {
          if (item !== undefined && item !== null && item !== "") {
            searchParams.append(key, String(item));
          }
        });
      } else {
        searchParams.append(key, String(val));
      }
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
};

const buildUrl = (
  endpoint: string,
  params?: Record<string, any>,
): { url: string; baseUrl: string; formattedEndpoint: string } => {
  const baseUrl = getBaseUrl();
  const formattedEndpoint = endpoint.startsWith("/")
    ? endpoint
    : `/${endpoint}`;

  const url = endpoint.startsWith("http")
    ? `${endpoint}${buildQueryString(params)}`
    : `${baseUrl}${formattedEndpoint}${buildQueryString(params)}`;

  return { url, baseUrl, formattedEndpoint };
};

/**
 * Checks API response body success and message to trigger appropriate toast notifications if showToast is enabled.
 */
function handleResponseToast(data: any, method: string, showToast: boolean = true) {
  if (!showToast || !data || typeof data !== "object") return;

  const isMutation = ["POST", "PUT", "PATCH", "DELETE"].includes(
    (method || "GET").toUpperCase(),
  );

  if (data.success === false) {
    if (data.message) {
      toast.error(data.message);
    }
  } else if (isMutation && data.message) {
    toast.success(data.message);
  }
}

async function parseResponse<T>(
  response: Response,
  method: string = "GET",
  showToast: boolean = true,
  responseType?: "json" | "blob" | "text",
): Promise<T> {
  if (response.status === 204) {
    return {} as T;
  }
  const contentType = response.headers.get("content-type") || "";
  let data: any;

  if (responseType === "blob") {
    data = await response.blob();
  } else if (responseType === "text") {
    data = await response.text();
  } else if (
    responseType === "json" ||
    contentType.includes("application/json")
  ) {
    data = await response.json();
  } else if (
    contentType.includes("application/pdf") ||
    contentType.includes("application/vnd.openxmlformats") ||
    contentType.includes("application/msword") ||
    contentType.includes("application/octet-stream")
  ) {
    data = await response.blob();
  } else {
    data = await response.blob();
  }

  handleResponseToast(data, method, showToast);

  if (
    data &&
    typeof data === "object" &&
    !(data instanceof Blob) &&
    data.success === false
  ) {
    const errMsg = data.message || "Request failed";
    throw new ApiError(errMsg, response.status, data);
  }

  return data as T;
}

async function parseErrorResponse(
  response: Response,
  showToast: boolean = true,
): Promise<{ errorMessage: string; errorData: any }> {
  let errorData: any;
  try {
    errorData = await response.json();
  } catch {
    errorData = await response.text();
  }

  const errorMessage =
    (typeof errorData === "object" && errorData?.message) ||
    `HTTP Error ${response.status}: ${response.statusText}`;

  if (showToast) {
    toast.error(errorMessage);
  }

  return { errorMessage, errorData };
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Performs refresh token API call and updates auth state.
 */
async function performTokenRefresh(baseUrl: string): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new ApiError("No refresh token available", 401);
  }

  const refreshUrl = `${baseUrl}/api/auth/refresh/`;
  const refreshRes = await fetch(refreshUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  if (!refreshRes.ok) {
    const { errorMessage, errorData } = await parseErrorResponse(refreshRes, true);
    throw new ApiError(errorMessage, refreshRes.status, errorData);
  }

  const refreshData = await refreshRes.json();
  const newAccessToken =
    refreshData?.data?.access ||
    refreshData?.access ||
    refreshData?.data?.token ||
    refreshData?.token;

  const newRefreshToken = refreshData?.data?.refresh || refreshData?.refresh;

  if (!newAccessToken) {
    throw new ApiError("Failed to obtain new access token from response", 401);
  }

  localStorage.setItem("token", newAccessToken);
  if (newRefreshToken) {
    localStorage.setItem("refreshToken", newRefreshToken);
  }

  queryClient.setQueryData(["authToken"], newAccessToken);

  return newAccessToken;
}

/**
 * Handles queueing concurrent requests and retrying failed requests when refreshing tokens.
 */
async function handleRefreshTokenAndRetry<T>(
  url: string,
  config: RequestInit,
  baseUrl: string,
  method: string = "GET",
  showToast: boolean = true,
  responseType?: "json" | "blob" | "text",
): Promise<T> {
  if (isRefreshing) {
    const newToken = await new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });

    const updatedConfig: RequestInit = {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${newToken}`,
      },
    };

    const retryRes = await fetch(url, updatedConfig);
    if (!retryRes.ok) {
      const { errorMessage, errorData } = await parseErrorResponse(retryRes, showToast);
      throw new ApiError(errorMessage, retryRes.status, errorData);
    }
    return parseResponse<T>(retryRes, method, showToast, responseType);
  }

  isRefreshing = true;

  try {
    const newAccessToken = await performTokenRefresh(baseUrl);
    processQueue(null, newAccessToken);
    isRefreshing = false;

    const retriedConfig: RequestInit = {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${newAccessToken}`,
      },
    };

    const retryResponse = await fetch(url, retriedConfig);
    if (!retryResponse.ok) {
      const { errorMessage, errorData } =
        await parseErrorResponse(retryResponse, showToast);
      throw new ApiError(errorMessage, retryResponse.status, errorData);
    }

    return await parseResponse<T>(retryResponse, method, showToast, responseType);
  } catch (refreshErr) {
    processQueue(refreshErr, null);
    isRefreshing = false;
    logoutUser();
    throw refreshErr;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    params,
    body,
    headers = {},
    showToast: userShowToast,
    responseType,
    ...customConfig
  } = options;
  const method = (customConfig.method || "GET").toUpperCase();

  const defaultShowToast = method !== "GET";
  const showToast =
    userShowToast !== undefined ? Boolean(userShowToast) : defaultShowToast;

  const { url, baseUrl, formattedEndpoint } = buildUrl(endpoint, params);
  const token = getAuthToken();
  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;

  const defaultHeaders: Record<string, string> = {
    Accept: responseType === "blob" ? "*/*" : "application/json",
  };

  if (!isFormData) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  if (token) {
    defaultHeaders["Authorization"] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...customConfig,
    headers: {
      ...defaultHeaders,
      ...headers,
    },
  };

  if (body) {
    config.body = isFormData
      ? body
      : typeof body === "string"
        ? body
        : JSON.stringify(body);
  }

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      if (response.status === 401) {
        const isAuthEndpoint = formattedEndpoint.includes("/api/auth");
        const refreshToken = getRefreshToken();

        if (!isAuthEndpoint && refreshToken) {
          return await handleRefreshTokenAndRetry<T>(
            url,
            config,
            baseUrl,
            method,
            showToast,
            responseType,
          );
        } else if (!isAuthEndpoint) {
          logoutUser();
        }
      }

      const { errorMessage, errorData } = await parseErrorResponse(
        response,
        showToast,
      );
      throw new ApiError(errorMessage, response.status, errorData);
    }

    return await parseResponse<T>(response, method, showToast, responseType);
  } catch (error) {
    const apiError = handleApiError(error);
    if (showToast && !(error instanceof ApiError)) {
      toast.error(apiError.message);
    }
    throw apiError;
  }
}

// HTTP request verbs
export async function httpGet<T>(
  endpoint: string,
  params?: Record<string, any>,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(endpoint, { ...options, method: "GET", params });
}

export async function httpPost<T>(
  endpoint: string,
  data?: any,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(endpoint, { ...options, method: "POST", body: data });
}

export async function httpPut<T>(
  endpoint: string,
  data?: any,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(endpoint, { ...options, method: "PUT", body: data });
}

export async function httpPatch<T>(
  endpoint: string,
  data?: any,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(endpoint, { ...options, method: "PATCH", body: data });
}

export async function httpDelete<T>(
  endpoint: string,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(endpoint, { ...options, method: "DELETE" });
}

// Export default https object wrapper
export const https = {
  get: httpGet,
  post: httpPost,
  put: httpPut,
  patch: httpPatch,
  delete: httpDelete,
};

export default https;
